import React, { useState, useMemo, useRef, useEffect } from "react";

/** Utility badges for score and course count */
const ScoreBadge = ({ score }) => {
  let badgeClasses = "bg-gray-100 text-gray-800";
  if (score !== undefined) {
    if (score >= 80) badgeClasses = "bg-green-100 text-green-800";
    else if (score >= 50) badgeClasses = "bg-yellow-100 text-yellow-800";
    else badgeClasses = "bg-red-100 text-red-800";
  }
  return (
    <span
      className={`${badgeClasses} text-xs font-medium me-2 px-2.5 py-0.5 rounded-full`}
    >
      {score !== undefined ? `${score}/100` : "N/A"}
    </span>
  );
};

const CourseCountBadge = ({ count }) => (
  <span className="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">
    {count} {count === 1 ? "Course" : "Courses"}
  </span>
);

/** Mini Schedule preview for each combination tile */
const MiniSchedule = ({ combination, breaks, parseTime, plannedCourses }) => {
  // Holds the currently hovered group for visual highlighting.
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const days = ["Mo", "Tu", "We", "Th", "Fr"];

  // Helper: generate a stable course key.
  const courseKey = (course) =>
    `${course.dept}-${course.number}`.toLowerCase();

  // Color mapping for planned courses.
  const colorOptions = [
    "bg-red-400",
    "bg-green-400",
    "bg-blue-400",
    "bg-yellow-300",
    "bg-purple-400",
    "bg-pink-400",
    "bg-indigo-400",
    "bg-teal-400",
    "bg-orange-400",
  ];
  const courseColors = {};
  plannedCourses.forEach((course, index) => {
    courseColors[courseKey(course)] =
      colorOptions[index % colorOptions.length];
  });

  // Initialize timeline boundaries and events storage.
  let minTime = Infinity;
  let maxTime = 0;
  const eventsByDay = {};
  days.forEach((day) => {
    eventsByDay[day] = [];
  });

  // Process course events.
  combination.forEach((courseOption) => {
    if (courseOption.sfuData && courseOption.sfuData.courseSchedule) {
      courseOption.sfuData.courseSchedule.forEach((sched) => {
        if (sched.isExam) return; // Skip exam times.
        // Skip if either time is missing or marked as "TBA".
        if (
          !sched.startTime ||
          !sched.endTime ||
          sched.startTime.toLowerCase() === "tba" ||
          sched.endTime.toLowerCase() === "tba"
        ) {
          return;
        }
        const dayArr = sched.days
          ? sched.days.split(",").map((d) => d.trim())
          : [];
        const start = parseTime(sched.startTime);
        const end = parseTime(sched.endTime);

        // Update timeline boundaries.
        if (start < minTime) minTime = start;
        if (end > maxTime) maxTime = end;

        const key = courseKey(courseOption);
        dayArr.forEach((day) => {
          if (eventsByDay[day]) {
            eventsByDay[day].push({
              start,
              end,
              color: courseColors[key] || "bg-blue-400",
              tooltip: `${courseOption.dept.toUpperCase()} ${courseOption.number} (${sched.startTime} - ${sched.endTime})`,
              courseKey: key,
              group: key,
            });
          }
        });
      });
    }
  });

  // Process break events.
  breaks
    .filter((bk) => bk.selected)
    .forEach((bk) => {
      // Assumes break times are valid.
      const start = parseTime(bk.start);
      const end = parseTime(bk.end);

      if (start < minTime) minTime = start;
      if (end > maxTime) maxTime = end;

      (bk.days || []).forEach((day) => {
        if (eventsByDay[day]) {
          eventsByDay[day].push({
            start,
            end,
            color: bk.color || "bg-gray-400",
            tooltip: `Break: ${bk.name} (${bk.start} - ${bk.end})`,
            courseKey: null,
            group: `break-${bk.name.toLowerCase()}`,
          });
        }
      });
    });

  // Set default boundaries if no events were processed.
  if (minTime === Infinity) {
    minTime = 8 * 60; // 8:00 AM
    maxTime = 17 * 60; // 5:00 PM
  }

  const pixelsPerMinute = 0.5;
  const timelineHeight = (maxTime - minTime) * pixelsPerMinute;

  // Style for striped breaks.
  const stripedBreakStyle = {
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(255,255,255,0.3), rgba(255,255,255,0.3) 10px, transparent 10px, transparent 20px)",
  };

  return (
    <div>
      {/* Days Header */}
      <div className="mb-1">
        <div className="flex justify-between bg-gray-200 text-gray-700 text-xs font-medium px-2 py-1 rounded-t">
          {days.map((day, idx) => (
            <span key={idx} className="w-full text-center">
              {day}
            </span>
          ))}
        </div>
      </div>

      {/* Timeline Grid */}
      <div className="relative" style={{ height: timelineHeight }}>
        <div className="grid grid-cols-5 h-full">
          {days.map((day, dayIndex) => (
            <div
              key={dayIndex}
              className={`relative border-r border-gray-200 ${
                dayIndex === 0 ? "border-l" : ""
              }`}
              style={{ height: timelineHeight }}
            >
              {(eventsByDay[day] || [])
                .sort((a, b) => a.start - b.start)
                .map((event, idx, arr) => {
                  const gap = 2; // Gap between back-to-back events.
                  let top = (event.start - minTime) * pixelsPerMinute;
                  let height = (event.end - event.start) * pixelsPerMinute;

                  // Add a small gap if events are nearly back-to-back.
                  if (idx > 0) {
                    const prevEvent = arr[idx - 1];
                    const prevBottom =
                      (prevEvent.end - minTime) * pixelsPerMinute;
                    if (Math.abs(top - prevBottom) < 1) {
                      top += gap;
                      height = Math.max(height - gap, 2);
                    }
                  }

                  return (
                    <div
                      key={idx}
                      className={`absolute left-1 right-1 rounded text-xs transition-all duration-200 cursor-pointer ${
                        event.group && hoveredGroup === event.group
                          ? "ring-1 ring-gray-300"
                          : ""
                      } hover:ring-1 hover:ring-gray-300`}
                      style={{ top: `${top}px`, height: `${height}px` }}
                      title={event.tooltip}
                      onMouseEnter={() => {
                        if (event.group) setHoveredGroup(event.group);
                      }}
                      onMouseLeave={() => setHoveredGroup(null)}
                    >
                      <div
                        className={`${event.color} h-full rounded hover:opacity-90`}
                        style={event.courseKey === null ? stripedBreakStyle : {}}
                      />
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function MiniScheduleModal({
  open,
  onClose,
  combinations,
  breaks,
  parseTime,
  plannedCourses,
  onSelect,
  isComputing,
}) {
  const minScoreRef = useRef(null);
  const maxCoursesRef = useRef(null);

  const [appliedMinScore, setAppliedMinScore] = useState("");
  const [appliedMaxCourses, setAppliedMaxCourses] = useState("");

  const [searchComputing, setSearchComputing] = useState(false);

  // Close modal on pressing Escape.
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Filter combinations based on local state.
  const filteredCombinations = useMemo(() => {
    return combinations.filter((combo) => {
      if (
        appliedMinScore !== "" &&
        combo.score < parseInt(appliedMinScore, 10)
      ) {
        return false;
      }
      if (
        appliedMaxCourses !== "" &&
        combo.combination.length > parseInt(appliedMaxCourses, 10)
      ) {
        return false;
      }
      return true;
    });
  }, [combinations, appliedMinScore, appliedMaxCourses]);

  if (!open) return null;

  const showLoading = isComputing || searchComputing;

  const handleSearch = () => {
    setSearchComputing(true);
    setTimeout(() => {
      if (minScoreRef.current && maxCoursesRef.current) {
        setAppliedMinScore(minScoreRef.current.value);
        setAppliedMaxCourses(maxCoursesRef.current.value);
      }
      setSearchComputing(false);
    }, 200);
  };

  return (
    <div
      id="mini-schedule-modal"
      tabIndex="-1"
      aria-hidden="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
    >
      <div className="relative p-4 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div
          className="relative bg-white rounded-lg shadow dark:bg-gray-700 min-h-[500px]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Schedule Overview
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-4">
            {/* Filters */}
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex items-center">
                <label className="mr-1 text-sm text-gray-700 dark:text-gray-300">
                  Min Score:
                </label>
                <input
                  type="number"
                  ref={minScoreRef}
                  placeholder="50"
                  className="w-16 text-sm p-1 border rounded"
                />
              </div>
              <div className="flex items-center">
                <label className="mr-1 text-sm text-gray-700 dark:text-gray-300">
                  Max Courses:
                </label>
                <input
                  type="number"
                  ref={maxCoursesRef}
                  placeholder="4"
                  className="w-16 text-sm p-1 border rounded"
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                className="ml-2 px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Search
              </button>
            </div>

            {/* Loading or Schedules */}
            {showLoading ? (
              <div className="flex items-center justify-center h-56 w-full">
                <div className="px-3 py-1 text-xs font-medium text-blue-800 bg-blue-200 rounded-full animate-pulse dark:bg-blue-900 dark:text-blue-200">
                  loading...
                </div>
              </div>
            ) : filteredCombinations.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {filteredCombinations.map((combo, index) => (
                  <div
                    key={index}
                    className="p-2 border rounded cursor-pointer transition duration-100 bg-white dark:bg-gray-800 hover:ring hover:ring-gray-300"
                    onClick={() => onSelect(index)}
                  >
                    {/* Score + Course Count Badges */}
                    <div className="flex items-center justify-between mb-2">
                      <ScoreBadge score={combo.score} />
                      <CourseCountBadge count={combo.combination.length} />
                    </div>
                    {/* Mini Schedule Preview */}
                    <MiniSchedule
                      combination={combo.combination}
                      breaks={breaks}
                      parseTime={parseTime}
                      plannedCourses={plannedCourses}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-56 text-center text-gray-500">
                No schedules match the filter criteria.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MiniScheduleModal;
